import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useMasterData } from '../../hooks/useMasterData';
import { workOrderService } from '../../services/workOrder/workOrderService';
import { CreateWorkOrderDto } from '../../types/workOrder';
import { Wrench, Calendar, DollarSign } from 'lucide-react';

interface CreateWorkOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  complaintId?: string;
  onSuccess: () => void;
}

export const CreateWorkOrderModal: React.FC<CreateWorkOrderModalProps> = ({
  isOpen,
  onClose,
  complaintId = 'CMP-2026-00101',
  onSuccess,
}) => {
  const { categories } = useMasterData();
  const [formData, setFormData] = useState<Partial<CreateWorkOrderDto>>({
    complaint_id: complaintId,
    work_category_name: 'Air Conditioning & HVAC',
    vendor_name: 'CoolTech HVAC Solutions',
    supervisor_name: 'Supervisor Suresh',
    description: '',
    scope_of_work: '',
    estimated_cost: 250,
    target_date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const res = await workOrderService.createWorkOrder({
      complaint_id: formData.complaint_id!,
      work_category_name: formData.work_category_name!,
      vendor_name: formData.vendor_name,
      supervisor_name: formData.supervisor_name,
      description: formData.description || 'Facility maintenance work order',
      scope_of_work: formData.scope_of_work || 'Execute repair according to scope',
      estimated_cost: Number(formData.estimated_cost),
      target_date: formData.target_date!,
    });

    if (res.success) {
      onSuccess();
      onClose();
    }
    setIsSubmitting(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Issue Work Order for ${complaintId}`}>
      <form onSubmit={handleSubmit} className="space-y-4 text-xs text-slate-300">
        <div>
          <label className="block font-medium text-slate-300 mb-1">Parent Complaint ID</label>
          <input
            type="text"
            disabled
            value={formData.complaint_id}
            className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs font-mono text-sky-400"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block font-medium text-slate-300 mb-1">Work Specialty Category *</label>
            <select
              value={formData.work_category_name}
              onChange={e => setFormData({ ...formData, work_category_name: e.target.value })}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-100"
            >
              {categories.map(c => (
                <option key={c.category_id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-medium text-slate-300 mb-1">Assigned Vendor / Contractor *</label>
            <select
              value={formData.vendor_name}
              onChange={e => setFormData({ ...formData, vendor_name: e.target.value })}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-100"
            >
              <option value="CoolTech HVAC Solutions">CoolTech HVAC Solutions</option>
              <option value="Alpha Plumbing & Piping">Alpha Plumbing & Piping</option>
              <option value="ElectroPower Services">ElectroPower Services</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block font-medium text-slate-300 mb-1">Estimated Cost ($) *</label>
            <input
              type="number"
              required
              value={formData.estimated_cost}
              onChange={e => setFormData({ ...formData, estimated_cost: Number(e.target.value) })}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-100"
            />
          </div>

          <div>
            <label className="block font-medium text-slate-300 mb-1">Target Completion Date *</label>
            <input
              type="date"
              required
              value={formData.target_date}
              onChange={e => setFormData({ ...formData, target_date: e.target.value })}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-100"
            />
          </div>
        </div>

        <div>
          <label className="block font-medium text-slate-300 mb-1">Work Description *</label>
          <input
            type="text"
            required
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief title of work order scope..."
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-100"
          />
        </div>

        <div>
          <label className="block font-medium text-slate-300 mb-1">Detailed Scope of Work</label>
          <textarea
            rows={3}
            value={formData.scope_of_work}
            onChange={e => setFormData({ ...formData, scope_of_work: e.target.value })}
            placeholder="Step by step technical scope required from vendor..."
            className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-xs text-slate-100"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" isLoading={isSubmitting} icon={<Wrench className="w-4 h-4" />}>
            Issue Work Order
          </Button>
        </div>
      </form>
    </Modal>
  );
};
